import * as React from 'react';
import { cn } from '../../styles/lib/utils';

export type ExpandableTextProps = Omit<React.ComponentProps<'div'>, 'children'> & {
  children: string;
  /** Lines shown while collapsed. */
  lines?: number;
  expanded?: boolean;
  defaultExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  moreLabel?: string;
  lessLabel?: string;
  toggleTestId?: string;
  /** Classes for the collapsed "… More" overlay; set its background to match the surface behind the text. */
  toggleClassName?: string;
};

/** Text clamped to a number of lines, with an inline More/Less toggle shown only when it overflows. */
function ExpandableText({
  children,
  lines = 3,
  expanded: expandedProp,
  defaultExpanded = false,
  onExpandedChange,
  moreLabel = 'More',
  lessLabel = 'Less',
  toggleTestId,
  toggleClassName,
  className,
  ...props
}: ExpandableTextProps) {
  const [uncontrolledExpanded, setUncontrolledExpanded] = React.useState(defaultExpanded);
  const expanded = expandedProp ?? uncontrolledExpanded;
  const textRef = React.useRef<HTMLParagraphElement>(null);
  const [overflows, setOverflows] = React.useState(false);
  const textId = React.useId();
  const toggleRef = React.useRef<HTMLButtonElement>(null);
  const refocusToggle = React.useRef(false);

  // More and Less are separate buttons, so focus moves to the new one after a click swaps them.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs after each expand or collapse, once the new toggle has mounted
  React.useEffect(() => {
    if (!refocusToggle.current) return;
    refocusToggle.current = false;
    toggleRef.current?.focus();
  }, [expanded]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: children and lines change the clamped height, so overflow is re-measured when they change
  React.useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || expanded) return;
    const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [expanded, children, lines]);

  const setExpanded = (next: boolean) => {
    refocusToggle.current = true;
    if (expandedProp === undefined) setUncontrolledExpanded(next);
    onExpandedChange?.(next);
  };

  const showToggle = overflows || expanded;

  return (
    <div data-slot="expandable-text" className={cn('relative text-xs text-grey-90', className)} {...props}>
      <p
        ref={textRef}
        id={textId}
        className="whitespace-pre-wrap break-words"
        style={
          expanded
            ? undefined
            : { display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: lines, overflow: 'hidden' }
        }
      >
        {children}
        {expanded && showToggle && (
          <>
            {' '}
            <button
              ref={toggleRef}
              type="button"
              aria-expanded
              aria-controls={textId}
              onClick={() => setExpanded(false)}
              className="cursor-pointer rounded-[2px] text-blue-50 underline hover:text-blue-60 focus-ring"
              data-testid={toggleTestId}
            >
              {lessLabel}
            </button>
          </>
        )}
      </p>
      {!expanded && showToggle && (
        <span className={cn('absolute right-0 bottom-0 bg-white pl-1', toggleClassName)}>
          <span aria-hidden="true">… </span>
          <button
            ref={toggleRef}
            type="button"
            aria-expanded={false}
            aria-controls={textId}
            onClick={() => setExpanded(true)}
            className="cursor-pointer rounded-[2px] text-blue-50 underline hover:text-blue-60 focus-ring"
            data-testid={toggleTestId}
          >
            {moreLabel}
          </button>
        </span>
      )}
    </div>
  );
}

export { ExpandableText };
